import { useMemo, useState } from "react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Heart, RotateCcw, X } from "lucide-react";
import { cn } from "@/lib/utils";

type MarketingTinderAngleDemoProps = {
  className?: string;
};

type DemoAngle = {
  title: string;
  angle: string;
  purpose: string;
  match: number;
  channel: string;
};

const demoAngles: DemoAngle[] = [
  {
    title: "Get relevant content ideas based on your favorite topics",
    angle: "Position Arcitext as the assistant that understands your guidelines and rewrites content on command.",
    purpose: "Designed for an email nurture or webinar follow-up.",
    match: 88,
    channel: "linkedin",
  },
  {
    title: "Save hours of second guessing your text",
    angle: "Show how Arcitext keeps every piece of content locked to the right tone, no matter who is writing it.",
    purpose: "Great for website copy or short-form video script.",
    match: 96,
    channel: "website",
  },
  {
    title: "Lock in your writing style or brand voice",
    angle: "Highlight the instantly shareable AI audits that decode brand archetypes for teams.",
    purpose: "Perfect for a LinkedIn carousel or founder newsletter.",
    match: 91,
    channel: "email",
  },

];

const channelLabels: Record<string, string> = {
  website: "Website Copy",
  linkedin: "LinkedIn",
  email: "Email",
};

export const MarketingTinderAngleDemo = ({ className }: MarketingTinderAngleDemoProps) => {
  const [index, setIndex] = useState(0);
  const [likes, setLikes] = useState<number[]>([]);

  const angle = demoAngles[index];
  const likedCount = likes.length;

  const nextIndex = useMemo(() => (index + 1) % demoAngles.length, [index]);
  const prevIndex = useMemo(() => (index - 1 + demoAngles.length) % demoAngles.length, [index]);

  const handlePass = () => {
    setIndex(nextIndex);
  };

  const handleLike = () => {
    setLikes((current) => (current.includes(index) ? current : [...current, index]));
    setIndex(nextIndex);
  };

  const handleUndo = () => {
    setLikes((current) => current.filter((value) => value !== prevIndex));
    setIndex(prevIndex);
  };

  return (
    <Card className={cn("w-full max-w-md bg-gradient-to-br from-background to-muted/20 border-2 shadow-xl", className)}>
      <CardHeader className="pb-3 space-y-3">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Idea {index + 1} of {demoAngles.length}
          </span>
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="font-medium">Match {angle.match}%</span><Badge variant="secondary">{channelLabels[angle.channel] ?? angle.channel}</Badge>
          </span>

        </div>

        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-lg leading-tight text-foreground">{angle.title}</CardTitle>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        <section className="space-y-2">
          <h4 className="text-sm font-medium text-foreground">Angle</h4>
          <div className="space-y-2">
            <div className="h-3 w-11/12 rounded bg-foreground/10" />
            <div className="h-3 w-5/6 rounded bg-foreground/10" />
            <div className="h-3 w-4/5 rounded bg-foreground/10" />
          </div>
        </section>

        <section className="space-y-2">
          <h4 className="text-sm font-medium text-foreground">Purpose</h4>
          <div className="space-y-2">
            <div className="h-3 w-10/12 rounded bg-foreground/10" />
            <div className="h-3 w-3/4 rounded bg-foreground/10" />
          </div>
        </section>
      </CardContent>

      <CardFooter className="flex items-center justify-center gap-2 pt-0">

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="lg"
            className="h-12 w-12 rounded-full p-0 hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
            onClick={handlePass}
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Skip angle</span>
          </Button>
          <Button
            size="lg"
            className="h-12 w-12 rounded-full p-0"
            variant="default"
            onClick={handleLike}
          >
            <Heart className="h-5 w-5" />
            <span className="sr-only">Save angle</span>
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default MarketingTinderAngleDemo;
